import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { map, catchError, switchMap, filter, take } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { AuthService } from '../../Auth/Service/auth.service';
import { RolesService } from '../Service/roles.service';
import { ProfileService } from '../../Profile/Service/profile.service';
import { NAVIGATION_ITEMS } from '../Models/roles.model';

/**
 * Role-based route guard for Angular 19
 * 
 * Usage in routes:
 * {
 *   path: 'admin',
 *   canActivate: [rolesGuard],
 *   data: { roleNamesAllowed: ['admin', 'superAdmin'] },
 *   component: AdminComponent
 * }
 */
export const rolesGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const rolesService = inject(RolesService);
  const profileService = inject(ProfileService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    router.navigate(['/login'], { 
      queryParams: { returnUrl: state.url } 
    });
    return false;
  }

  return profileService.getProfileState().pipe(
    filter(profileState => !profileState.isLoading),
    take(1),
    switchMap((profileState) => {
      // If no profile after loading completes, redirect to login
      // This could happen if auto-fetch failed (expired token handled by interceptor)
      if (!profileState.profile) {
        router.navigate(['/login'], { 
          queryParams: { returnUrl: state.url } 
        });
        return of(false);
      }

      const allowedRoles = route.data['roleNamesAllowed'] as string[];
      
      if (!allowedRoles || allowedRoles.length === 0) {
        return of(true);
      }

      return rolesService.getRoles().pipe(
        map((userRoles) => {
          const userRoleNames = userRoles.map(role => role.roleName);
          const hasRequiredRole = allowedRoles.some(role => userRoleNames.includes(role));
          
          if (!hasRequiredRole) {
            if (userRoleNames.length === 0) {
              router.navigate(['/login']);
              return false;
            }
            
            const availableNavItems = NAVIGATION_ITEMS.filter(item => 
              item.requiredRoles.some(role => userRoleNames.includes(role))
            );
            
            if (availableNavItems.length === 0) {
              router.navigate(['/login']);
              return false;
            }
            
            // Redirect to first available route
            const redirectPath = `/${availableNavItems[0].path}`;
            router.navigate([redirectPath]);
            return false;
          }
          
          return true;
        }),
        catchError(() => {
          router.navigate(['/login']);
          return of(false);
        })
      );
    })
  );
};

/**
 * Factory function for creating role guards with specific roles
 * 
 * Usage:
 * {
 *   path: 'admin',
 *   canActivate: [createRoleGuard(['admin', 'superAdmin'])],
 *   component: AdminComponent
 * }
 */
export const createRoleGuard = (allowedRoles: string[]): CanActivateFn => {
  return (route, state) => {
    const authService = inject(AuthService);
    const rolesService = inject(RolesService);
    const router = inject(Router);

    if (!authService.isAuthenticated()) {
      router.navigate(['/login'], { 
        queryParams: { returnUrl: state.url } 
      });
      return false;
    }

    const hasRequiredRole = rolesService.hasAnyRole(allowedRoles);
    
    if (!hasRequiredRole) {
      const userRoleNames = rolesService.getRoleNames();
      
      const availableNavItems = NAVIGATION_ITEMS.filter(item => 
        item.requiredRoles.some(role => userRoleNames.includes(role))
      );
      
      const redirectPath = availableNavItems.length > 0 ? availableNavItems[0].path : '/institutions';
      router.navigate([redirectPath]);
      return false;
    }
    
    return true;
  };
};
